import type { Agent } from '@/types/Agents';
import { getDefaultAgentTab, hasCrm, isCrmAgentTab } from '@/utils/crmUtils';

/**
 * The agent page's tabs, in the order they are rendered.
 *
 * This is the single source of truth for both the tab strip and the document
 * title, so a tab's label only ever has to be renamed in one place.
 */
export const AGENT_TABS = [
  {
    value: 'calendar',
    labelKey: 'agent.page.calendar.tab.translation',
    fallbackLabel: 'Calendar',
  },
  {
    value: 'conversations',
    labelKey: 'agent.page.conversations.button.translation',
    fallbackLabel: 'Conversations',
  },
  {
    value: 'contacts',
    labelKey: 'common.contacts',
    fallbackLabel: 'Contacts',
  },
  {
    value: 'business',
    labelKey: 'agent.page.business.button.translation',
    fallbackLabel: 'Business',
  },
  {
    value: 'usage',
    labelKey: 'agent.page.usage.button.translation',
    fallbackLabel: 'Usage',
  },
  {
    value: 'settings',
    labelKey: 'agent.page.settings.button.translation',
    fallbackLabel: 'Settings',
  },
] as const;

export type AgentTabValue = (typeof AGENT_TABS)[number]['value'];

const AGENT_TAB_VALUES: readonly string[] = AGENT_TABS.map(tab => tab.value);

/**
 * `?tab=statistics` is the old spelling of the usage tab and still turns up in
 * bookmarks and emailed links. Anything else unrecognised resolves to null so
 * the caller falls back to the agent's default tab rather than rendering an
 * empty panel.
 */
export function normalizeAgentTabParam(
  tabParam: string | null | undefined
): AgentTabValue | null {
  const tab = tabParam === 'statistics' ? 'usage' : tabParam;
  return tab && AGENT_TAB_VALUES.includes(tab) ? (tab as AgentTabValue) : null;
}

/**
 * Where an agent lands with no `?tab=` in the URL: an agent that still needs a
 * subscription opens on settings, where the plans live, unless one has already
 * been requested and is waiting on us.
 */
export function getDefaultAgentTabForAgent(
  agent: Agent | null | undefined
): AgentTabValue {
  const status = agent?.subscription?.status;
  if (agent && status !== 'active' && status !== 'requested') return 'settings';
  return getDefaultAgentTab(agent);
}

/** The tab actually showing, given the URL's `?tab=` and the agent's entitlements. */
export function resolveAgentTab(
  agent: Agent | null | undefined,
  tabParam: string | null | undefined
): AgentTabValue {
  const requested = normalizeAgentTabParam(tabParam);
  if (agent && isCrmAgentTab(requested) && !hasCrm(agent)) {
    return 'conversations';
  }
  return requested ?? getDefaultAgentTabForAgent(agent);
}
